package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Customer
import com.solevo.dist.application.models.Order
import com.solevo.dist.network.firebase.api.orders.OrderFirebaseRemoteSource
import com.solevo.dist.network.firebase.api.user.UserFirebaseRemoteSource
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.flow.flowOf
import kotlinx.datetime.*
import org.koin.mp.KoinPlatform.getKoin

class OrdersViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val orderRemoteSource: OrderFirebaseRemoteSource = getKoin().get()
    private val userRemoteSource: UserFirebaseRemoteSource = getKoin().get()
    private val customerRepository: ApplicationCustomerRepository = getKoin().get()

    private val _state = MutableStateFlow(OrdersUiState())
    val state: StateFlow<OrdersUiState> = _state.asStateFlow()

    private var allOrders: List<Order> = emptyList()
    private var currentCustomer: Customer? = null

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        if (_state.value.loading.not() && allOrders.isNotEmpty()) return
        _state.value = _state.value.copy(loading = true, error = null)

        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        customerRepository.getCurrentCustomer()
                            .combine(orderRemoteSource.getOrdersLive()) { customer, orders ->
                                Pair(customer, orders)
                            }
                    } else {
                        flowOf(Pair(null, emptyList<Order>()))
                    }
                }
                .onEach { (customer, orders) ->
                    currentCustomer = customer
                    allOrders = orders

                    computeAndEmit()
                }
                .catch { t ->
                    _state.value = _state.value.copy(
                        loading = false,
                        error = t.message ?: "Unknown error",
                        rows = emptyList()
                    )
                }
                .collect()
        }
    }

    fun stop() = scope.cancel()

    fun selectTab(tab: OrdersTab) {
        if (_state.value.selectedTab == tab) return
        _state.value = _state.value.copy(selectedTab = tab)
        computeAndEmit()
    }

    fun toggleSortOrder() {
        val newSortOrder = when (_state.value.sortOrder) {
            SortOrder.Descending -> SortOrder.Ascending
            SortOrder.Ascending -> SortOrder.Descending
        }
        _state.value = _state.value.copy(sortOrder = newSortOrder)
        computeAndEmit()
    }

    private fun computeAndEmit() {
        val tab = _state.value.selectedTab
        val filtered = when (tab) {
            OrdersTab.All -> allOrders
            OrdersTab.Created -> allOrders.filter { it.status is Order.Status.Created }
            OrdersTab.Confirmed -> allOrders.filter { it.status is Order.Status.Confirmed }
            OrdersTab.Expired -> allOrders.filter { it.status is Order.Status.Expired }
            OrdersTab.Closed -> allOrders.filter { it.status is Order.Status.Closed }
            OrdersTab.Cancelled -> allOrders.filter { it.status is Order.Status.Cancelled }
            OrdersTab.Error -> allOrders.filter { it.status is Order.Status.Error }
        }

        val sorted = when (_state.value.sortOrder) {
            SortOrder.Ascending -> filtered.sortedBy { it.orderDate }
            SortOrder.Descending -> filtered.sortedByDescending { it.orderDate }
        }

        val rows = sorted.map { it.toRow() }

        _state.value = _state.value.copy(
            loading = false,
            error = null,
            rows = rows
        )
    }

    /* ---------------- UI mapping helpers ---------------- */

    private fun Order.toRow(): OrderRowUi {
        val statusInfo = StatusMapper.mapOrderStatus(status)
        val currency = currentCustomer?.currency ?: "XOF"
        return OrderRowUi(
            id = orderId,
            amountText = AmountFormatter.formatAmountCompact(totalAmount, currency),
            statusText = statusInfo.label,
            statusColorHex = statusInfo.colorHex,
            description = if (!orderReference.isNullOrBlank()) "Order $orderReference" else "Order $orderId",
            pickupDateText = DateFormatter.formatDateOrDash(pickupDateTo),
            createdDateText = DateFormatter.formatDateOrDash(orderDate)
        )
    }
}

/* ------------ UI state / models ------------ */

enum class OrdersTab(val label: String) {
    All("All"), Created("Created"), Confirmed("Confirmed"), Expired("Expired"), Closed(
        "Closed"
    ),
    Cancelled("Cancelled"), Error("Error")
}

data class OrderRowUi(
    val id: String,
    val amountText: String,
    val statusText: String,
    val statusColorHex: String,
    val description: String,
    val pickupDateText: String,
    val createdDateText: String
)

enum class SortOrder {
    Ascending, Descending
}

data class OrdersUiState(
    val loading: Boolean = true,
    val rows: List<OrderRowUi> = emptyList(),
    val selectedTab: OrdersTab = OrdersTab.All,
    val sortOrder: SortOrder = SortOrder.Descending,
    val error: String? = null
)